/**
 * Authentication Guard Utilities
 * @description Tenant/Admin 저장소 분리, 영역별 로그인 페이지 리다이렉트
 */

import { redirect } from '@tanstack/react-router';
import { getQueryParam } from '@shared/router';
import { config } from '@shared/config';
import { getTenantAuthStorage, getAdminAuthStorage } from '@/utils/authStorage';

const LOGIN_TENANT = '/login';
const LOGIN_ADMIN = '/admin/login';

/**
 * Tenant 영역 인증 필수 (Entry 등)
 * Tenant 저장소만 검사, 없으면 /login
 */
export async function requireAuth(context?: { location?: { pathname?: string; search?: unknown } }): Promise<void> {
  const storage = getTenantAuthStorage();
  const storedUser = storage.getUser();
  const storedToken = storage.getToken();

  if (!storedUser || !storedToken) {
    const pathname =
      context?.location?.pathname ??
      (typeof window !== 'undefined' ? window.location.pathname : '/entry');
    const search =
      typeof window !== 'undefined'
        ? window.location.search
        : typeof context?.location?.search === 'string'
          ? context.location.search
          : '';
    const isMcpOAuth =
      pathname.endsWith('/mcp/oauth/callback') ||
      (search.includes('mcp_oauth=1') && search.includes('code_challenge'));
    if (isMcpOAuth) {
      const returnPath = `${pathname}${search}`;
      throw redirect({
        to: LOGIN_TENANT,
        search: { returnUrl: returnPath },
      });
    }
    throw redirect({ to: LOGIN_TENANT });
  }

  // 라우트 진입 가드에서는 네트워크 검증(/auth/me) 대신 저장소 정보만 검사한다.
  // 서버 검증 실패로 뒤로가기/로고 이동이 로그인 화면으로 튕기는 문제를 방지하기 위함.
  if (storedUser.userType !== 'tenant-user') {
    storage.clear();
    throw redirect({ to: LOGIN_TENANT });
  }
}

/**
 * Portal 관리자 권한 확인 (Admin 저장소만)
 * 없으면 /admin/login
 */
export async function requirePortalAdmin(): Promise<void> {
  const storage = getAdminAuthStorage();
  const storedUser = storage.getUser();
  const storedToken = storage.getToken();

  if (!storedUser || !storedToken) {
    throw redirect({ to: LOGIN_ADMIN });
  }

  if (storedUser.userType !== 'portal-admin') {
    throw redirect({ to: '/entry' });
  }
}

/**
 * Tenant 사용자 권한 확인 (Tenant 저장소만)
 */
export async function requireTenantUser(): Promise<void> {
  await requireAuth();

  const storage = getTenantAuthStorage();
  const storedUser = storage.getUser();
  if (storedUser && storedUser.userType !== 'tenant-user') {
    storage.clear();
    throw redirect({ to: LOGIN_TENANT });
  }
}

/**
 * Admin 페이지 접근 권한
 * - Admin 저장소(portal-admin) OK
 * - Tenant 저장소 + admin_service.service_management 권한 OK
 * - 그 외 → /admin/login
 */
export async function requireAdminAccess(context?: { location?: any }): Promise<void> {
  const adminStorage = getAdminAuthStorage();
  const tenantStorageForCheck = getTenantAuthStorage();

  const adminUser = adminStorage.getUser();
  const adminToken = adminStorage.getToken();
  const tenantUser = tenantStorageForCheck.getUser();
  const tenantToken = tenantStorageForCheck.getToken();

  // Portal Admin
  if (adminUser && adminToken && adminUser.userType === 'portal-admin') {
    try {
      const response = await fetch(`${config.apiUrl}/api/portal/admin/auth/me`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      }).catch(() => null);
      if (response?.ok) return;
    } catch {
      // fall through
    }
    adminStorage.clear();
  }

  // Tenant User with permission
  if (tenantUser && tenantToken && tenantUser.userType === 'tenant-user' && tenantUser.tenantId) {
    let tab: string | null = null;
    if (context?.location?.search) {
      tab = new URLSearchParams(context.location.search).get('tab');
    } else {
      tab = getQueryParam('tab');
    }
    const pathname = context?.location?.pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');

    // 구 경로: Workflow 설정 전용 UI로 리다이렉트
    if (pathname === '/admin/workflow-settings') {
      throw redirect({ to: '/workflow-settings' });
    }

    if (tab === 'service') {
      try {
        const response = await fetch(
          `${config.apiUrl}/api/portal/tenants/${tenantUser.tenantId}/authz/check`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${tenantToken}`,
              'X-Tenant-Id': tenantUser.tenantId,
            },
            body: JSON.stringify({
              serviceId: 'admin_service',
              action: 'service_management',
            }),
          }
        );

        if (response.status === 401) {
          tenantStorageForCheck.clear();
          throw redirect({ to: LOGIN_ADMIN });
        }
        if (response.ok) {
          const result = await response.json();
          if (result.allowed) return;
        }
      } catch (error) {
        if (error && typeof error === 'object' && 'to' in error) throw error;
      }
    }

    // 다른 tab은 tenant-user로 접근 불가
    throw redirect({ to: '/entry' });
  }

  throw redirect({ to: LOGIN_ADMIN });
}

/**
 * Workflow 설정 전용 페이지 접근 권한 (/workflow-settings)
 * - Portal 관리자(Admin 저장소) OK
 * - Tenant 사용자 + it-service-request view 권한 OK
 * - 그 외 → /entry 또는 /admin/login
 */
export async function requireWorkflowSettingsAccess(context?: { location?: { pathname?: string } }): Promise<void> {
  const adminStorage = getAdminAuthStorage();
  const tenantStorage = getTenantAuthStorage();

  const adminUser = adminStorage.getUser();
  const adminToken = adminStorage.getToken();
  const tenantUser = tenantStorage.getUser();
  const tenantToken = tenantStorage.getToken();

  if (adminUser && adminToken && adminUser.userType === 'portal-admin') {
    try {
      const response = await fetch(`${config.apiUrl}/api/portal/admin/auth/me`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      }).catch(() => null);
      if (response?.ok) return;
    } catch {
      // fall through
    }
    adminStorage.clear();
  }

  if (tenantUser && tenantToken && tenantUser.userType === 'tenant-user' && tenantUser.tenantId) {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/portal/tenants/${tenantUser.tenantId}/authz/check`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tenantToken}`,
            'X-Tenant-Id': tenantUser.tenantId,
          },
          body: JSON.stringify({ serviceId: 'it-service-request', action: 'view' }),
        }
      );
      if (response.status === 401) {
        tenantStorage.clear();
        throw redirect({ to: LOGIN_ADMIN });
      }
      if (response.ok) {
        const result = await response.json();
        if (result.allowed) return;
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'to' in error) throw error;
    }
    throw redirect({ to: '/entry' });
  }

  throw redirect({ to: '/entry' });
}
